import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Edit, Trash2, CheckCircle, XCircle, Download, Plus } from 'lucide-react';
import { productsApi } from '../../lib/api';
import PageHeader from '../../components/PageHeader';
import DataTable from '../../components/DataTable';
import Modal from '../../components/Modal';

interface Product {
  id: string;
  productCode: string;
  name: string;
  category: string;
  storageType: string;
  part: string;
  origin: string;
  avgWeight: string;
  isActive: boolean;
}

export default function ProductList() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [formData, setFormData] = useState({
    productCode: '',
    name: '',
    category: '牛',
    storageType: 'chilled',
    part: '',
    origin: '',
    avgWeight: '',
    remarks: '',
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['products', search],
    queryFn: () => productsApi.getAll({ search }),
  });

  const createMutation = useMutation({
    mutationFn: productsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('商品を登録しました');
      setIsModalOpen(false);
      resetForm();
    },
    onError: () => toast.error('登録に失敗しました'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => productsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('商品を更新しました');
      setIsModalOpen(false);
      resetForm();
    },
    onError: () => toast.error('更新に失敗しました'),
  });

  const deleteMutation = useMutation({
    mutationFn: productsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('商品を削除しました');
    },
    onError: () => toast.error('削除に失敗しました'),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      await Promise.all(ids.map(id => productsApi.delete(id)));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('選択した商品を削除しました');
    },
    onError: () => toast.error('一括削除に失敗しました'),
  });

  const bulkStatusMutation = useMutation({
    mutationFn: async ({ ids, isActive }: { ids: string[]; isActive: boolean }) => {
      await Promise.all(ids.map(id => productsApi.update(id, { isActive })));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('ステータスを更新しました');
    },
    onError: () => toast.error('ステータス更新に失敗しました'),
  });

  const resetForm = () => {
    setFormData({
      productCode: '',
      name: '',
      category: '牛',
      storageType: 'chilled',
      part: '',
      origin: '',
      avgWeight: '',
      remarks: '',
    });
    setEditingProduct(null);
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setFormData({
      productCode: product.productCode || '',
      name: product.name,
      category: product.category,
      storageType: product.storageType,
      part: product.part || '',
      origin: product.origin || '',
      avgWeight: product.avgWeight || '',
      remarks: '',
    });
    setIsModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingProduct) {
      updateMutation.mutate({ id: editingProduct.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (id: string) => {
    if (confirm('この商品を削除しますか？')) {
      deleteMutation.mutate(id);
    }
  };

  const handleBulkDelete = (ids: string[]) => {
    if (confirm(`${ids.length}件の商品を削除しますか？`)) {
      bulkDeleteMutation.mutate(ids);
    }
  };

  const handleBulkActivate = (ids: string[]) => {
    bulkStatusMutation.mutate({ ids, isActive: true });
  };

  const handleBulkDeactivate = (ids: string[]) => {
    bulkStatusMutation.mutate({ ids, isActive: false });
  };

  const handleExport = () => {
    const items = data?.data?.data || [];
    if (items.length === 0) {
      toast.error('エクスポートするデータがありません');
      return;
    }
    const headers = ['コード', '商品名', 'カテゴリ', '保管区分', '部位', '産地', '平均重量', '状態'];
    const rows = items.map((item: Product) => [
      item.productCode || '',
      item.name || '',
      item.category || '',
      item.storageType === 'chilled' ? 'チルド' : '冷凍',
      item.part || '',
      item.origin || '',
      item.avgWeight || '',
      item.isActive ? '有効' : '無効',
    ]);
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `商品マスタ_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('CSVをエクスポートしました');
  };

  const columns = [
    { key: 'productCode', label: 'コード', width: '100px', sortable: true },
    { key: 'name', label: '商品名', sortable: true },
    { key: 'category', label: 'カテゴリ', width: '100px', sortable: true },
    {
      key: 'storageType',
      label: '保管区分',
      width: '100px',
      sortable: true,
      render: (row: Product) => (
        <span className={row.storageType === 'chilled' ? 'badge badge-chilled' : 'badge badge-frozen'}>
          {row.storageType === 'chilled' ? 'チルド' : '冷凍'}
        </span>
      ),
    },
    { key: 'part', label: '部位', width: '100px' },
    { key: 'origin', label: '産地', width: '100px' },
    { key: 'avgWeight', label: '平均重量', width: '100px' },
    {
      key: 'isActive',
      label: '状態',
      width: '80px',
      sortable: true,
      render: (row: Product) => (
        <span className={row.isActive ? 'badge badge-success' : 'badge badge-error'}>
          {row.isActive ? '有効' : '無効'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '操作',
      width: '100px',
      render: (row: Product) => (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); handleEdit(row); }}
            className="p-1.5 rounded-lg transition-colors hover:bg-gray-100"
            style={{ color: 'var(--accent-color)' }}
            title="編集"
          >
            <Edit size={16} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleDelete(row.id); }}
            className="p-1.5 rounded-lg transition-colors hover:bg-gray-100"
            style={{ color: 'var(--error-color)' }}
            title="削除"
          >
            <Trash2 size={16} />
          </button>
        </div>
      ),
    },
  ];

  const bulkActions = [
    {
      label: '有効化',
      icon: <CheckCircle size={14} />,
      onClick: handleBulkActivate,
      variant: 'primary' as const,
    },
    {
      label: '無効化',
      icon: <XCircle size={14} />,
      onClick: handleBulkDeactivate,
      variant: 'outline' as const,
    },
    {
      label: '一括削除',
      icon: <Trash2 size={14} />,
      onClick: handleBulkDelete,
      variant: 'danger' as const,
    },
  ];

  return (
    <div className="animate-fadeIn">
      <PageHeader
        title="商品マスタ"
        breadcrumbs={[{ label: 'マスタ管理' }]}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="商品名で検索..."
        onApply={() => refetch()}
        actions={
          <div className="flex items-center gap-1.5">
            <button className="btn btn-outline btn-sm" onClick={handleExport}>
              <Download size={14} />
            </button>
            <button className="btn btn-primary btn-sm" onClick={() => { resetForm(); setIsModalOpen(true); }}>
              <Plus size={14} />
              商品登録
            </button>
          </div>
        }
      />

      <DataTable
        columns={columns}
        data={data?.data?.data || []}
        loading={isLoading}
        selectable
        bulkActions={bulkActions}
        getRowId={(row: Product) => row.id}
        emptyMessage="商品が登録されていません"
        defaultSortKey="name"
        defaultSortOrder="asc"
      />

      <Modal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); resetForm(); }}
        title={editingProduct ? '商品編集' : '商品登録'}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">商品コード</label>
              <input
                type="text"
                className="input-field"
                value={formData.productCode}
                onChange={(e) => setFormData({ ...formData, productCode: e.target.value })}
              />
            </div>
            <div>
              <label className="label">カテゴリ <span className="text-red-500">*</span></label>
              <select
                className="input-field"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                required
              >
                <option value="牛">牛</option>
                <option value="豚">豚</option>
                <option value="鶏">鶏</option>
                <option value="その他">その他</option>
              </select>
            </div>
          </div>

          <div>
            <label className="label">商品名 <span className="text-red-500">*</span></label>
            <input
              type="text"
              className="input-field"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">保管区分 <span className="text-red-500">*</span></label>
              <select
                className="input-field"
                value={formData.storageType}
                onChange={(e) => setFormData({ ...formData, storageType: e.target.value })}
                required
              >
                <option value="chilled">チルド</option>
                <option value="frozen">冷凍</option>
              </select>
            </div>
            <div>
              <label className="label">部位</label>
              <input
                type="text"
                className="input-field"
                value={formData.part}
                onChange={(e) => setFormData({ ...formData, part: e.target.value })}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">産地</label>
              <input
                type="text"
                className="input-field"
                value={formData.origin}
                onChange={(e) => setFormData({ ...formData, origin: e.target.value })}
              />
            </div>
            <div>
              <label className="label">平均重量 (kg)</label>
              <input
                type="number"
                step="0.01"
                className="input-field"
                value={formData.avgWeight}
                onChange={(e) => setFormData({ ...formData, avgWeight: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label className="label">備考</label>
            <textarea
              className="input-field"
              rows={3}
              value={formData.remarks}
              onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => { setIsModalOpen(false); resetForm(); }}
            >
              キャンセル
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {editingProduct ? '更新' : '登録'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
